<template>
    <div class="table-row summary-row" v-if="records && records.length > 0">
        <!-- Sol taraftaki özet başlık hücresi -->
        <div 
            class="table-cell summary-label-cell"
            :style="{
                minWidth: getColumnWidth('record-id') + 'px',
                maxWidth: getColumnWidth('record-id') + 'px'
            }"
        >Summary</div>

        <!-- Her field için özet hücresi -->
        <div 
            class="table-cell" 
            v-for="(field, index) in visibleFields" 
            :key="field._id"
            :style="{
                minWidth: getColumnWidth(field._id) + 'px',
                maxWidth: getColumnWidth(field._id) + 'px'
            }"
        >
            <!-- Dropdown alan özeti -->
            <template v-if="field.fieldType === 'dropdown'">
                <div class="dropdown-summary">
                    <div
                        v-for="dist in getDropdownDistribution(records, field)"
                        :key="dist.value"
                        class="summary-option-chip"
                        :style="{
                            backgroundColor: dist.color,
                            flexGrow: dist.flexValue,
                        }"
                        v-tippy="{ content: getDropdownTooltip(dist) }"
                        @mouseover="hoverOption(dist)"
                        @mouseleave="leaveOption(dist)"
                    >
                        <!-- Renk barı olarak boş içerik -->
                    </div>
                </div>
            </template>

            <!-- Kullanıcı tipi alanlar için pasta grafik -->
            <template v-else-if="isUserField(field)">
                <div class="pie-chart-summary" ref="userChart"></div>
            </template>

            <!-- Sayısal alanlar için sum + tooltip -->
            <template v-else-if="field.valueType === 'number' || field.fieldType === 'currency'">
                <span class="numeric-summary" v-tippy="{ content: getNumericTooltip(records, field) }">
                    Sum: {{ getNumericSum(records, field) }}
                </span>
            </template>

            <!-- Tarihsel alanlar için min - max format + tooltip -->
            <template v-else-if="field.valueType === 'date'">
                <span class="date-summary-chip" v-tippy="{ content: getDateRangeTooltip(records, field) }">
                    {{ getShortDateRange(records, field) }}
                </span>
            </template>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import * as echarts from "echarts";

export default Vue.extend({
    name: "TableFooterSummary",
    props: {
        records: {
            type: Array,
            required: true,
        },
        visibleFields: {
            type: Array,
            required: true,
        },
        columnWidths: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            hoveredValue: null,
            userCharts: {},
        };
    },
    methods: {
        //---------------------------------------
        // 1) Dropdown Summaries
        //---------------------------------------
        getDropdownDistribution(records, field) {
            var distribution = {};
            var totalCount = 0;
            for (var i = 0; i < records.length; i++) {
                var record = records[i];
                var value = record.data[field._id];
                if (Array.isArray(value)) {
                    for (var j = 0; j < value.length; j++) {
                        var multiVal = value[j];
                        if (!distribution[multiVal]) {
                            distribution[multiVal] = 0;
                        }
                        distribution[multiVal]++;
                        totalCount++;
                    }
                } else {
                    if (!value) continue;
                    if (!distribution[value]) {
                        distribution[value] = 0;
                    }
                    distribution[value]++;
                    totalCount++;
                }
            }

            // Statik modda seçenek renklerini alabilmek için
            var options = [];
            if (field.options && field.options.dropdown && field.options.dropdown.mode === "static") {
                options = field.options.dropdown.options;
            }

            var resultArray = [];
            for (var key in distribution) {
                if (distribution.hasOwnProperty(key)) {
                    var count = distribution[key];
                    var colorVal = this.findOptionColor(key, options);
                    if (!colorVal) {
                        colorVal = this.generateColorFromText(key);
                    }
                    var ratio = count / totalCount;
                    resultArray.push({
                        value: key,
                        count: count,
                        color: colorVal,
                        flexValue: ratio * 10,
                        ratio: ratio,
                    });
                }
            }

            // Büyükten küçüğe sırala
            resultArray.sort(function (a, b) {
                return b.count - a.count;
            });

            return resultArray;
        },
        findOptionColor(value, options) {
            if (!options || !options.length) {
                return null;
            }
            for (var i = 0; i < options.length; i++) {
                if (options[i].value === value) {
                    return options[i].color;
                }
            }
            return null;
        },
        generateColorFromText(text) {
            var hash = 0;
            for (var i = 0; i < text.length; i++) {
                hash = text.charCodeAt(i) + ((hash << 5) - hash);
            }
            var hue = Math.abs(hash) % 360;
            var saturation = 25 + (Math.abs(hash) % 15);
            var lightness = 85 + (Math.abs(hash) % 10);
            return "hsl(" + hue + ", " + saturation + "%, " + lightness + "%)";
        },

        // Dropdown tooltip içeriği
        getDropdownTooltip(dist) {
            // Yüzdeyi hesaplayıp metne ekleyelim
            var percentage = (dist.ratio * 100).toFixed(2);
            return dist.value + " | Count: " + dist.count + " | Percentage: " + percentage + "%";
        },

        hoverOption(dist) {
            this.hoveredValue = dist.value;
        },
        leaveOption(dist) {
            this.hoveredValue = null;
        },

        //---------------------------------------
        // 2) Numeric Summaries
        //---------------------------------------
        getNumericSum(records, field) {
            var sum = 0;
            var validCount = 0;
            for (var i = 0; i < records.length; i++) {
                var val = records[i].data[field._id];
                if (val || val === 0) {
                    var numericVal = parseFloat(val);
                    if (!isNaN(numericVal)) {
                        sum += numericVal;
                        validCount++;
                    }
                }
            }
            return sum.toFixed(2);
        },
        getNumericTooltip(records, field) {
            // Sum, avg, min, max, count hesaplayalım
            var sum = 0;
            var count = 0;
            var minVal = null;
            var maxVal = null;

            for (var i = 0; i < records.length; i++) {
                var val = records[i].data[field._id];
                if (val || val === 0) {
                    var numericVal = parseFloat(val);
                    if (!isNaN(numericVal)) {
                        sum += numericVal;
                        count++;
                        if (minVal === null || numericVal < minVal) {
                            minVal = numericVal;
                        }
                        if (maxVal === null || numericVal > maxVal) {
                            maxVal = numericVal;
                        }
                    }
                }
            }
            var avg = count > 0 ? (sum / count).toFixed(2) : "-";

            return (
                "Sum: " +
                sum.toFixed(2) +
                " | Avg: " +
                avg +
                " | Min: " +
                (minVal !== null ? minVal.toFixed(2) : "-") +
                " | Max: " +
                (maxVal !== null ? maxVal.toFixed(2) : "-") +
                " | Count: " +
                count
            );
        },

        //---------------------------------------
        // 3) Date Summaries
        //---------------------------------------
        getShortDateRange(records, field) {
            var minDate = null;
            var maxDate = null;

            for (var i = 0; i < records.length; i++) {
                var val = records[i].data[field._id];
                if (val) {
                    var dateObj = moment(val);
                    if (dateObj.isValid()) {
                        if (!minDate || dateObj.isBefore(minDate)) {
                            minDate = dateObj;
                        }
                        if (!maxDate || dateObj.isAfter(maxDate)) {
                            maxDate = dateObj;
                        }
                    }
                }
            }
            if (minDate && maxDate) {
                return minDate.format("MMM DD") + " - " + maxDate.format("MMM DD");
            }
            return "";
        },

        // Tarihsel alanlar için tooltip içeriği
        getDateRangeTooltip(records, field) {
            var range = this.calculateDateRange(records, field);
            if (!range) {
                return "Date data not found";
            }
            var dayDiff = range.maxDate.diff(range.minDate, "days");
            if (dayDiff === 0) {
                return "Same day";
            } else if (dayDiff === 1) {
                return "1 day interval";
            } else {
                return dayDiff + " day interval";
            }
        },

        // Min - max tarihleri hesaplayan yardımcı metod
        calculateDateRange(records, field) {
            var minDate = null;
            var maxDate = null;

            for (var i = 0; i < records.length; i++) {
                var val = records[i].data[field._id];
                if (val) {
                    var dateObj = moment(val);
                    if (dateObj.isValid()) {
                        if (!minDate || dateObj.isBefore(minDate)) {
                            minDate = dateObj;
                        }
                        if (!maxDate || dateObj.isAfter(maxDate)) {
                            maxDate = dateObj;
                        }
                    }
                }
            }

            if (!minDate || !maxDate) {
                return null;
            }
            return { minDate: minDate, maxDate: maxDate };
        },

        //---------------------------------------
        // 4) User Field Summaries (Pie Chart)
        //---------------------------------------
        isUserField(field) {
            return (
                field.fieldType === "user" ||
                field.fieldType === "record_created_by" ||
                field.fieldType === "record_updated_by"
            );
        },

        getUserDistribution(records, field) {
            var distribution = {};
            var totalCount = 0;
            var userNames = {};

            for (var i = 0; i < records.length; i++) {
                var record = records[i];
                var value = record.data[field._id];

                if (!value) {
                    if (!distribution["none"]) {
                        distribution["none"] = 0;
                        this.users = this.users || {};
                        this.users["none"] = {
                            name: "Boş",
                            _id: "none",
                        };
                    }
                    
                } else {
                    if (!distribution[value._id]) {
                        distribution[value._id] = 0;
                        this.users = this.users || {};
                        this.users[value._id] = value;
                    }
                }

                distribution[value ? value._id : "none"]++;
                totalCount++;
            }

            var resultArray = [];
            for (var key in distribution) {
                if (distribution.hasOwnProperty(key)) {
                    var count = distribution[key];
                    var ratio = count / totalCount;

                    resultArray.push({
                        name: this.users[key].name,
                        value: count,
                        itemId: key,
                        ratio: ratio,
                        color: this.generateColorFromText(key),
                    });
                }
            }

            // Büyükten küçüğe sırala
            resultArray.sort(function (a, b) {
                return b.value - a.value;
            });

            return resultArray;
        },

        createUserChart(field, element) {
            const data = this.getUserDistribution(this.records, field);
            if (!data.length) return;

            const chart = echarts.init(element);
            const option = {
                tooltip: {
                    trigger: "item",
                    formatter: (params) => {
                        const percent = params.percent.toFixed(1);
                        return `${params.name}<br/>${params.value} (${percent}%)`;
                    },
                },
                series: [
                    {
                        type: "pie",
                        radius: "85%",
                        itemStyle: {
                            borderRadius: 2,
                            borderColor: "#fff",
                            borderWidth: 1,
                        },
                        label: {
                            show: false,
                        },
                        data: data.map((item) => ({
                            name: item.name,
                            value: item.value,
                            itemStyle: {
                                color: item.color,
                            },
                        })),
                    },
                ],
            };

            chart.setOption(option);
            this.userCharts[field._id] = chart;

            return chart;
        },

        initUserCharts() {
            this.$nextTick(() => {
                if (!this.$refs.userChart) return;

                const elements = this.$refs.userChart;
                if (!Array.isArray(elements)) {
                    const userFields = this.visibleFields.filter((field) => this.isUserField(field));
                    if (userFields.length > 0) {
                        this.createUserChart(userFields[0], elements);
                    }
                    return;
                }

                const userFields = this.visibleFields.filter((field) => this.isUserField(field));
                elements.forEach((el, index) => {
                    if (index < userFields.length) {
                        this.createUserChart(userFields[index], el);
                    }
                });
            });
        },
        
        // Kolon genişlik fonksiyonu
        getColumnWidth(fieldId) {
            // Record ID kolonu için daha küçük varsayılan genişlik
            if (fieldId === 'record-id') {
                return this.columnWidths[fieldId] || 100;
            }
            return this.columnWidths[fieldId] || 245;
        },
    },
    mounted() {
        this.initUserCharts();
    },
    updated() {
        this.initUserCharts();
    },
    beforeDestroy() {
        // Destroy all charts
        Object.values(this.userCharts).forEach((chart) => {
            if (chart) {
                chart.dispose();
            }
        });
    },
});
</script>

<style scoped lang="scss">
.summary-row {
    background-color: #f3f3f3;
    font-weight: bold;

    .summary-label-cell {
        justify-content: center;
        font-weight: bold;
    }
}

.dropdown-summary {
    width: 100%;
    height: 18px;
    display: flex;
    align-items: center;
    border: 1px solid #ddd;
    border-radius: 4px;

    .summary-option-chip {
        height: 100%;
        transition: transform 0.2s ease;
        cursor: pointer;

        &:hover {
            transform: scale(1.2);
            height: 120%;
        }
    }
}

.numeric-summary {
    color: #333;
}

.date-summary-chip {
    display: inline-block;
    padding: 2px 6px;
    background-color: #d9f7be;
    border-radius: 4px;
    cursor: default;
}

.pie-chart-summary {
    width: 48px;
    height: 48px;
    margin: 0 auto;
}

.table-cell {
    &.summary-label-cell {
        // Genişlik artık dinamik olarak ayarlanacak
    }
}
</style>
